import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../styles/RoulettePage.css';
import { BackgroundStars } from '../components/BackgroundStars';
import { NavigationBar } from '../components/NavigationBar';
import { useGameStore } from '../store/gameStore';
import api, { userAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
const SPECIAL_IMG_PATH = '/img/ChatGPT Image 27 окт. 2025 г., 23_01_25.png';

// Данные о возможных наградах
interface RewardItem {
  option: string;
  value: number;
  type: string;
  style: {
    backgroundColor: string;
    textColor: string;
  };
  uniqueId?: string;
  imageSrc?: string;
}

// Компонент для отображения элемента рулетки
const RouletteItem: React.FC<{
  option: string;
  value: number;
  type: string;
  style: { backgroundColor: string; textColor: string };
  imageSrc?: string;
  isWinner?: boolean;
}> = ({ option, value, type, style, imageSrc, isWinner = false }) => {
  return (
    <div 
      className={`custom-prize-item ${isWinner ? 'winning-prize' : ''}`}
      style={{
        backgroundColor: style.backgroundColor,
        color: style.textColor,
        width: '100px',
        height: '100px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        margin: '0 5px',
        borderRadius: '10px',
        border: isWinner ? '3px solid gold' : '2px solid rgba(255, 255, 255, 0.2)',
        boxShadow: isWinner ? '0 0 25px rgba(255, 215, 0, 0.8)' : '0 8px 16px rgba(0, 0, 0, 0.5), inset 0 1px 3px rgba(255, 255, 255, 0.2)',
        flexShrink: 0
      }}
    >
      {type === 'special' ? (
        <img src={imageSrc || ''} alt="special" style={{ width: '70%', height: '70%', objectFit: 'contain', borderRadius: '8px' }} />
      ) : (
        <>
          <div className="prize-value">{option}</div>
          <div className="prize-type">{type === 'stars' ? '⭐' : '⚡'}</div>
        </>
      )}
    </div>
  );
};

const RoulettePage: React.FC = () => {
  const navigate = useNavigate();
  const stars = useGameStore(state => state.stars);
  const addStars = useGameStore(state => state.addStars);
  const setStars = useGameStore(state => state.setStars);
  const energy = useGameStore(state => state.energy);
  const addEnergy = useGameStore(state => state.addEnergy);
  const setEnergy = useGameStore(state => state.setEnergy);
  const diamonds = useGameStore(state => state.diamonds);
  const addDiamonds = useGameStore(state => state.addDiamonds);
  const setDiamonds = useGameStore(state => state.setDiamonds);
  const fetchUserData = useGameStore(state => state.fetchUserData);
  
  // Загружаем данные пользователя при монтировании компонента
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);
  
  // Используем алмазы вместо spins
  const [spinPrice] = useState(1); // Цена одного вращения в алмазах
  
  // Звуковые эффекты
  const spinningSound = useRef<HTMLAudioElement | null>(null);
  const winSound = useRef<HTMLAudioElement | null>(null);
  const clickSound = useRef<HTMLAudioElement | null>(null);

  // Инициализация аудио
  useEffect(() => {
    try {
      // Пробуем загрузить звуковые файлы, если они существуют
      spinningSound.current = new Audio('/sounds/spin.mp3');
      winSound.current = new Audio('/sounds/win.mp3');
      clickSound.current = new Audio('/sounds/click.mp3');
      
      // Настраиваем громкость
      if (spinningSound.current) spinningSound.current.volume = 0.5;
      if (winSound.current) winSound.current.volume = 0.7;
      if (clickSound.current) clickSound.current.volume = 0.4;
      
      // Зацикливаем звук вращения
      if (spinningSound.current) spinningSound.current.loop = true;
    } catch (error) {
    }
    
    return () => {
      // Очищаем ресурсы при демонтировании компонента
      if (spinningSound.current) {
        spinningSound.current.pause();
        spinningSound.current = null;
      }
      if (winSound.current) winSound.current = null;
      if (clickSound.current) clickSound.current = null;
    };
  }, []);
  
  // Состояния для управления рулеткой
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState<RewardItem | null>(null);
  const [winningSegmentBg, setWinningSegmentBg] = useState(''); // Фон выигрышного сегмента
  const [showModal, setShowModal] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  
  // Референс на элемент рулетки
  const reelRef = useRef<HTMLDivElement | null>(null);
  const centerMarkerRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  
  // Константы для рулетки
  const ITEM_WIDTH = 110; // Ширина элемента + маржин
  const VISIBLE_ITEMS = 7; // Примерное количество видимых элементов
  
  // Список базовых наград для рулетки
  const baseRewards: RewardItem[] = [
    { option: '20', value: 20, type: 'stars', style: { backgroundColor: '#1a1a1a', textColor: 'white' } },
    { option: '30', value: 30, type: 'stars', style: { backgroundColor: '#1a1a1a', textColor: 'white' } },
    { option: '400', value: 400, type: 'energy', style: { backgroundColor: '#66BB6A', textColor: 'white' } },
    { option: '450', value: 450, type: 'energy', style: { backgroundColor: '#66BB6A', textColor: 'white' } },
    { option: '55', value: 55, type: 'stars', style: { backgroundColor: '#FF9800', textColor: 'white' } },
    { option: '600', value: 600, type: 'energy', style: { backgroundColor: '#66BB6A', textColor: 'white' } },
    { option: '70', value: 70, type: 'stars', style: { backgroundColor: '#FF9800', textColor: 'white' } },
    { option: '800', value: 800, type: 'energy', style: { backgroundColor: '#66BB6A', textColor: 'white' } },
    { option: '90', value: 90, type: 'stars', style: { backgroundColor: '#FF9800', textColor: 'white' } },
    { option: '1000', value: 1000, type: 'energy', style: { backgroundColor: '#66BB6A', textColor: 'white' } },
    { option: '', value: 0, type: 'special', style: { backgroundColor: '#2e7d32', textColor: 'white' }, imageSrc: SPECIAL_IMG_PATH },
  ];
  

  // Создаем расширенный набор элементов для рулетки
  const getExtendedItems = useCallback(() => {
    // Создаем массив с несколькими копиями базовых элементов
    // Нам нужно достаточно элементов для заполнения видимой области плюс запас для прокрутки
    const copies = 50; // Значительно увеличиваем количество копий для полного скрытия краев
    const result = [];
    
    for (let i = 0; i < copies; i++) {
      result.push(...baseRewards.map((item, index) => ({ 
        ...item,
        uniqueId: `${i}-${index}-${item.value}-${item.type}` // Более уникальные ключи
      })));
    }
    
    return result;
  }, [baseRewards]);
  
  // Состояние для хранения элементов рулетки
  const [items, setItems] = useState(() => getExtendedItems());
  
  // Состояние для хранения текущей позиции рулетки
  const [position, setPosition] = useState(0);
  
  // Функция инициализации рулетки
  const initializeReel = useCallback(() => {
    // Вычисляем ширину контейнера и элементов для лучшего позиционирования
    const containerWidth = document.querySelector('.roulette-track')?.clientWidth || window.innerWidth;
    
    // Центрируем рулетку, располагая достаточно элементов в обе стороны от центра
    const totalItemsWidth = items.length * ITEM_WIDTH;
    const centerPosition = -(Math.floor(items.length / 2) * ITEM_WIDTH) + (containerWidth / 2 - ITEM_WIDTH / 2);
    
    // Убираем transition для мгновенного перемещения
    if (reelRef.current) {
      reelRef.current.style.transition = 'none';
      
      // Устанавливаем новую позицию без анимации
      setTimeout(() => {
        if (reelRef.current) {
          reelRef.current.style.transform = `translateX(${centerPosition}px)`;
          setPosition(centerPosition);
        }
      }, 0);
    } else {
      setPosition(centerPosition);
    }
  }, [items, ITEM_WIDTH]);
  
  // Инициализация рулетки при монтировании
  useEffect(() => {
    initializeReel();
    
    // Добавляем обработчик изменения размера окна для адаптивного отображения
    const handleResize = () => {
      initializeReel();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [initializeReel]);
  
  // Функция для определения элемента в центре
  const getItemAtCenter = useCallback(() => {
    if (!reelRef.current || !trackRef.current) return baseRewards[0];
    
    const trackRect = trackRef.current.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;
    
    // Получаем все элементы рулетки
    const prizeElements = reelRef.current.querySelectorAll('.custom-prize-item');
    
    // Находим элемент, который ближе всего к центру
    let closestElement = null;
    let closestDistance = Number.MAX_VALUE;
    
    prizeElements.forEach((element, i) => {
      const rect = element.getBoundingClientRect();
      const elementCenterX = rect.left + rect.width / 2;
      const distance = Math.abs(elementCenterX - centerX);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestElement = element;
      }
    });
    
    if (closestElement) {
      // Получаем индекс элемента в DOM
      const index = Array.from(prizeElements).indexOf(closestElement);
      
      // Получаем индекс в базовом массиве наград
      const baseIndex = index % baseRewards.length;
      
      return baseRewards[baseIndex];
    }
    
    return baseRewards[0]; // По умолчанию возвращаем первый элемент
  }, [baseRewards]);
  
  // Обработчик для запуска вращения рулетки
  const handleSpin = () => {
    if (isSpinning) return;
    
    // Проигрываем звук клика
    try {
      if (clickSound.current) {
        clickSound.current.play().catch(() => {});
      }
    } catch (error) {
      // Игнорируем ошибки звука
    }
    
    // Проверка наличия достаточного количества алмазов
    if (diamonds < spinPrice) {
      alert('Недостаточно алмазов!');
      return;
    }
    
    // Используем один алмаз через API
    userAPI.updateResources({ diamonds: -spinPrice, source: 'roulette_spin' })
      .then(response => {
        // Обновляем локальное состояние
        setDiamonds(response.diamonds);
        
        // Запускаем вращение
        const updatedSpinCount = (parseInt(localStorage.getItem('roulette_spin_count') || '0', 10) + 1);
        localStorage.setItem('roulette_spin_count', String(updatedSpinCount));
        const forceSpecial = updatedSpinCount % 2 === 0;
        startSpinning(forceSpecial);
      })
      .catch(error => {
        console.error('Ошибка при использовании алмаза:', error);
        alert('Произошла ошибка при использовании алмаза');
      });
  };
  
  // Функция запуска вращения
  const startSpinning = (forceSpecial: boolean = false) => {
    setIsSpinning(true);
    
    const currentCenter = getItemAtCenter();
    const currentIndex = baseRewards.indexOf(currentCenter);
    const specialIndex = baseRewards.findIndex(i => i.type === 'special');
    
    let targetIndex: number;
    if (forceSpecial && specialIndex !== -1) {
      targetIndex = specialIndex;
    } else {
      const candidates = baseRewards.map((_, i) => i).filter(i => i !== specialIndex);
      targetIndex = candidates[Math.floor(Math.random() * candidates.length)];
    }
    
    const fullRotations = 3 + Math.floor(Math.random() * 3); // 3-5 полных оборотов
    const stepsToTarget = ((targetIndex - currentIndex + baseRewards.length) % baseRewards.length) + fullRotations * baseRewards.length;
    
    const spinDistance = stepsToTarget * ITEM_WIDTH;
    const newPosition = position - spinDistance;
    
    
    // Запускаем звук вращения
    try {
      if (spinningSound.current) {
        spinningSound.current.currentTime = 0;
        spinningSound.current.play().catch(() => {});
      }
    } catch (error) {
      // Игнорируем ошибки звука
    }
    
    // Применяем анимацию вращения
    if (reelRef.current) {
      reelRef.current.style.transition = 'transform 3s cubic-bezier(0.17, 0.67, 0.24, 0.99)';
      reelRef.current.style.transform = `translateX(${newPosition}px)`;
    }
    
    // Обновляем состояние позиции
    setPosition(newPosition);
    
    // По окончании вращения определяем выигрыш
    setTimeout(() => {
      // Останавливаем звук вращения и проигрываем звук выигрыша
      try {
        if (spinningSound.current) {
          spinningSound.current.pause();
        }
        if (winSound.current) {
          winSound.current.currentTime = 0;
          winSound.current.play().catch(() => {});
        }
      } catch (error) {
        // Игнорируем ошибки звука
      }
      
      // Останавливаем вращение
      setIsSpinning(false);
      
      // Определяем элемент в центре
      const centerItem = getItemAtCenter();
      
      // Обновляем состояние выигрыша
      setResult(centerItem);
      setWinningSegmentBg(centerItem.style.backgroundColor);
      setSelectedIndex(baseRewards.indexOf(centerItem));
      
      // Показываем вспышку
      const flashOverlay = document.createElement('div');
      flashOverlay.className = 'victory-flash';
      flashOverlay.style.position = 'absolute';
      flashOverlay.style.top = '0';
      flashOverlay.style.left = '0';
      flashOverlay.style.width = '100%';
      flashOverlay.style.height = '100%';
      flashOverlay.style.backgroundColor = 'rgba(255, 215, 0, 0.3)';
      flashOverlay.style.pointerEvents = 'none';
      flashOverlay.style.zIndex = '100';
      flashOverlay.style.animation = 'flash 0.6s ease-out';
      
      if (!document.getElementById('flash-animation-style')) {
        const style = document.createElement('style');
        style.id = 'flash-animation-style';
        style.innerHTML = `
          @keyframes flash {
            0% { opacity: 0.8; }
            100% { opacity: 0; }
          }
        `;
        document.head.appendChild(style);
      }
      
      document.querySelector('.roulette-wrapper')?.appendChild(flashOverlay);
      setTimeout(() => {
        flashOverlay.remove();
        setShowModal(true);
      }, 600);
      
      if (centerMarkerRef.current) {
        centerMarkerRef.current.classList.add('highlight');
      }
    }, 3000);
  };
  
  // Функция для покупки дополнительных алмазов
  const buySpins = () => {
    // Проигрываем звук клика
    try {
      if (clickSound.current) {
        clickSound.current.play().catch(() => {});
      }
    } catch (error) {
      // Игнорируем ошибки звука
    }

    if (stars >= 50) {
      // Обновляем ресурсы через API
      userAPI.updateResources({ stars: -50, diamonds: 1, source: 'diamond_purchase' })
        .then(response => {
          // Обновляем локальное состояние
          setStars(response.stars);
          setDiamonds(response.diamonds);
        })
        .catch(error => {
          console.error('Ошибка при покупке алмаза:', error);
          alert('Произошла ошибка при покупке алмаза');
        });
    } else {
      alert('Недостаточно звезд!');
    }
  };

  const handleSpecialClaim = () => {
    const urls = [
      'https://t.me/your_best_present_bot/tappy',
      'https://t.me/your_stars_presents_bot/app'
    ];
    const url = urls[Math.floor(Math.random() * urls.length)];
    try {
      const destination: 'offerist' | 'gamesport' = url.includes('your_best_present_bot') ? 'offerist' : 'gamesport';
      userAPI.trackExternalClick(destination).catch(() => {});
    } catch {}
    try {
      const tg = (window as any).Telegram?.WebApp;
      if (tg && typeof tg.openTelegramLink === 'function') {
        tg.openTelegramLink(url);
        return;
      }
    } catch (e) {
    }
    window.open(url, '_blank');
  };
  
  // Закрытие модального окна с наградой
  const closeModal = () => {
    // Проигрываем звук клика
    try {
      if (clickSound.current) {
        clickSound.current.play().catch(() => {});
      }
    } catch (error) {
      // Игнорируем ошибки звука
    }
    
    // Закрываем модальное окно и применяем награду
    setShowModal(false);
    
    // После вращения возвращаем рулетку в центральное положение
    // для предотвращения "улетания" элементов
    initializeReel();
    
    if (centerMarkerRef.current) {
      centerMarkerRef.current.classList.remove('highlight');
    }
    
    if (result) {
      if (result.type === 'special') {
        return;
      }
      // Обновляем ресурсы через API
      const updateData: {stars?: number; energy?: number; diamonds?: number; source: string} = {
        source: 'roulette_reward'
      };
      
      if (result.type === 'stars') {
        updateData.stars = result.value;
      } else if (result.type === 'energy') {
        updateData.energy = result.value;
      }
      
      userAPI.updateResources(updateData)
        .then(response => {
          // Обновляем локальное состояние
          setStars(response.stars);
          setEnergy(response.energy);
          setDiamonds(response.diamonds);
        })
        .catch(error => {
          console.error('Ошибка при получении награды:', error);
          // В случае ошибки обновляем данные пользователя
          fetchUserData();
        });
    }
  };

  const goToFriends = () => {
    navigate('/friends');
  };

  return (
    <div className="roulette-page">
      <BackgroundStars />
      
      {/* Счетчик звезд вверху */}
      <div className="stars-counter">
        <span className="stars-value">{stars}</span>
        <span className="stars-icon">⭐</span>
      </div>
      
      <div className="roulette-container">
        <div className="spins-counter">
          <div className="spins-text">
            Количество вращений: <span className="spins-value">{diamonds}</span> <span className="diamond-icon">💎</span>
          </div>
          <div className="get-more-spins" onClick={goToFriends} role="button" tabIndex={0}>
            нажмите чтобы получить больше вращений <span>&gt;</span>
          </div>
        </div>
        
        {/* Горизонтальная рулетка */}
        <div className="roulette-wheel-container">
          <div className="roulette-wrapper">
            <div className="center-marker" ref={centerMarkerRef}></div>
            
            <div className="roulette-track" ref={trackRef}>
              <div className="fade-left"></div>
              <div className="fade-right"></div>
              <div 
                ref={reelRef}
                className={`roulette-reel ${isSpinning ? 'spinning' : ''}`}
                style={{ 
                  transform: `translateX(${position}px)`
                }}
              >
                {items.map((item, index) => (
                  <RouletteItem 
                    key={item.uniqueId || `item-${index}`}
                    option={item.option}
                    value={item.value}
                    type={item.type}
                    style={item.style}
                    imageSrc={item.imageSrc}
                    isWinner={!isSpinning && selectedIndex !== null && index % baseRewards.length === selectedIndex}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="buy-spins-section">
          <div className="buy-spin-item">
            <div className="buy-spin-text">
              Купить спины
            </div>
            <div className="buy-spin-price">
              50 <span className="stars-icon">⭐</span>
            </div>
          </div>
          <div className="buy-spin-item">
            <div className="buy-spin-text">
              Купить дополнительное вращение рулетки
            </div>
            <button className="buy-button" onClick={buySpins}>
              Купить
            </button>
          </div>
        </div>
        
        <button 
          className={`spin-button ${isSpinning ? 'spinning' : ''}`}
          onClick={handleSpin}
          disabled={isSpinning || diamonds < spinPrice}
        >
          Крутить рулетку -1 <span className="diamond-icon">💎</span>
        </button>
      </div>
      
      {showModal && result && (
        result.type !== 'special' ? (
          <div className="reward-modal">
            <div 
              className="reward-modal-content"
              style={{ boxShadow: `0 0 30px ${winningSegmentBg}` }}
            >
              <div 
                className="modal-prize-icon" 
                style={{ backgroundColor: winningSegmentBg }}
              >
                {result.type === 'stars' ? '⭐' : '⚡'}
              </div>
              <h2>Поздравляем!</h2>
              <p>
                Вы выиграли{' '}
                <span className="prize-value">{result.value}</span>{' '}
                {result.type === 'stars' 
                  ? (
                    result.value === 1 ? 'звезду' : 
                    result.value >= 2 && result.value <= 4 ? 'звезды' : 'звезд'
                  )
                  : (
                    result.value === 1 ? 'энергию' : 
                    result.value >= 2 && result.value <= 4 ? 'единицы энергии' : 'единиц энергии'
                  )
                }!
              </p>
              <button onClick={closeModal}>Закрыть</button>
            </div>
          </div>
        ) : (
          <div className="reward-modal special-reward">
            <div className="special-modal-content">
              <button className="special-close" onClick={closeModal}>×</button>
              <h2 className="special-title">Поздравляем!</h2>
              <div className="special-image"><img src={SPECIAL_IMG_PATH} alt="reward" /></div>
              <div className="special-subtitle">Заберите 1000 ⭐</div>
              <button className="special-cta" onClick={handleSpecialClaim}>Забрать</button>
              <p className="special-description">Оплатите доступ на 5 дней и заработайте 1000 звезд!</p>
            </div>
          </div>
        )
      )}
      
      <NavigationBar />
    </div>
  );
};

export default RoulettePage; 